import { useJob, useReview } from '../fetchers';
import {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { FeaturedJob, FeaturedReview } from 'rwl-shared/dist/models';

export interface ContentDetailsContextProps {
    currentIndex: number;
    itemCount: number;
    isDetailsOpen: boolean;
    hasMore: boolean;
    openDetails: (
        jobId: string | undefined,
        reviewId: string | undefined,
        allContent: FeaturedJob[] | FeaturedReview[],
        contentType: DetailsMode
    ) => void;
    mode: DetailsMode;
    onNext: () => void;
    setOnNext: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
    onPrev: () => void;
    setOnPrev: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
    closeDetails: () => void;
    setClose: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
}

const ContentDetailsContext = createContext<
    ContentDetailsContextProps | undefined
>(undefined);

interface ContentDetailsProviderProps {
    children: ReactNode;
}

export enum DetailsMode {
    JOB = 'job',
    REVIEW = 'review',
}
export const ContentDetailsProvider = ({
    children,
}: ContentDetailsProviderProps) => {
    const [isDetailsOpen, setDetailsOpen] = useState(false);
    const [mode, setMode] = useState<DetailsMode>(DetailsMode.JOB);
    const [onNext, setOnNext] = useState<() => void>();
    const [onPrev, setOnPrev] = useState<() => void>();
    const [close, setClose] = useState<() => void>();
    const [hasMore, setHasMore] = useState(false);
    const { clearReview, setReviewId, review } = useReview();
    const { clearJob, setJobId, job } = useJob();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [content, setContent] = useState<FeaturedJob[] | FeaturedReview[]>(
        []
    );
    const [itemCount, setItemCount] = useState(0);

    useEffect(() => {
        const currentItem = mode === DetailsMode.JOB ? job : review;
        let currentIndex = content.findIndex((c) => c.id === currentItem?.id);
        let allContent = content;
        if (currentIndex === -1) {
            currentIndex = 0;
            if (currentItem) {
                // The current item is not in the content list so it had to be fetched
                // Update the list to the fetched item
                allContent = [currentItem] as FeaturedJob[] | FeaturedReview[];
                setContent(allContent);
                if (currentItem && 'reviewId' in currentItem) {
                    setReviewId(currentItem?.reviewId);
                } else if (currentItem && 'jobId' in currentItem) {
                    setJobId(currentItem?.jobId);
                }
            }
        }
        setCurrentIndex(currentIndex);
        setHasMore(currentIndex < allContent.length - 1);
    }, [job, review]);

    const reset = () => {
        setContent([]);
        setCurrentIndex(-1);
        setHasMore(false);
        setJobId(undefined);
        setReviewId(undefined);
        setDetailsOpen(false);
        setMode(DetailsMode.JOB);
        setOnNext(undefined);
        setOnPrev(undefined);
        setClose(undefined);
        setItemCount(0);
        setDetailsOpen(false);
    };

    useEffect(() => {
        return reset;
    }, []);

    const handleNav = (newIndex: number) => {
        const currentItem = content[newIndex];
        if (mode === DetailsMode.JOB && currentItem?.id) {
            setJobId(currentItem?.id);
            // @ts-ignore - If we're here, we are looking at a FeaturedJob, but we don't want to assert reviewId because it could be undefined
            setReviewId(currentItem.reviewId);
        } else if (mode === DetailsMode.REVIEW && content[newIndex]?.id) {
            setReviewId(content[newIndex]?.id);
            // @ts-ignore - If we're here, we are looking at a FeaturedReview, but we don't want to assert jobId because it could be undefined
            setJobId(currentItem?.jobId);
        }
        setHasMore(newIndex < content.length - 1);
    };

    const contextValue: ContentDetailsContextProps = {
        currentIndex,
        hasMore,
        itemCount,
        isDetailsOpen,
        mode: mode,
        onNext: () => {
            setCurrentIndex((prev) => {
                const newIndex = (prev + 1) % content.length;
                handleNav(newIndex);
                return newIndex;
            });
            onNext?.();
        },
        onPrev: () => {
            setCurrentIndex((prev) => {
                const newIndex = (prev - 1) % content.length;
                handleNav(newIndex);
                return newIndex;
            });
            onPrev?.();
        },
        openDetails: (jobId, reviewId, content, mode) => {
            clearReview(); // It's possible to call 'open' while this is already open, so we need to clear the current review
            clearJob();
            setJobId(jobId);
            setReviewId(reviewId);
            const id = mode === DetailsMode.JOB ? jobId : reviewId;
            setMode(mode);
            setContent(content);
            const newIndex = content.findIndex(
                (c: FeaturedJob | FeaturedReview) => c.id === id
            );
            setCurrentIndex(newIndex);
            setItemCount(content.length);
            setDetailsOpen(true);
        },
        closeDetails: () => {
            close?.();
            reset();
        },
        setClose,
        setOnNext,
        setOnPrev,
    };

    return (
        <ContentDetailsContext.Provider value={contextValue}>
            {children}
        </ContentDetailsContext.Provider>
    );
};

export const useContentDetails = () => {
    const context = useContext(ContentDetailsContext);
    if (!context) {
        throw new Error(
            'useContentDetails must be used within a ContentDetailsProvider'
        );
    }
    return context;
};
